/**
 * Frontend ROUTE paths — the strings React Router matches in the browser.
 *
 * ── Not the API namespace, despite the name collision ──────────────────────
 *
 * The backend serves its webhook admin surface under `/api/webhook-events`
 * and `/api/webhook-failures` (`/api/webhooks/*` is provider ingress only).
 * Those are BACKEND paths, appended to the API base URL by the transport.
 * The route below is a CLIENT path handed to
 * `<Link to>` and matched by `App.tsx`. That both spell `/webhooks` is a
 * coincidence of naming, not a shared contract: either side can be renamed
 * without the other moving, and merging the two namespaces would tie an
 * in-browser URL to a server route that has no reason to stay in step.
 * Keep them apart.
 *
 * ── Why this producer exists (#1989) ───────────────────────────────────────
 *
 * The webhook-event detail path was hand-rolled at three production sites —
 * `components/ui/AuditTimeline.tsx`, `features/audit/widgets.tsx`, and
 * `features/admin/components/WebhookFailureDetailBody.tsx`. Neither #1979 nor
 * #1981 introduced the duplication on purpose; each simply added one more
 * literal next to an existing one, which is how a hand-rolled path spreads.
 * One producer plus the tripwire in
 * `__tests__/architecture/webhook-event-path-single-producer` stops the next
 * copy from landing.
 */

/**
 * The phone dispatch view (#2487) is one page with several views, so the view
 * rides a query param, not a second path. `_` keeps it out of the list-filter
 * namespace. The driver view (#2677) also carries the driver's Onfleet worker
 * id in `_driver`: the ETA report keys drivers by worker id, never by the local
 * driver id. The search view (#2678) carries its needle in `_q`, so a return
 * from a job detail finds the results still there.
 */
export const PHONE_VIEW_PARAM = '_phone';
export const PHONE_VIEW = { inbox: 'inbox', driver: 'driver', search: 'search' } as const;
export const PHONE_DRIVER_PARAM = '_driver';
export const PHONE_SEARCH_PARAM = '_q';
/**
 * The driver view shows its jobs as a list or on a map (#2679). The map rides
 * `_stops=map`, so a return from a job detail lands on the map again.
 */
export const PHONE_STOPS_VIEW_PARAM = '_stops';
export const PHONE_STOPS_VIEW = { map: 'map' } as const;

/** One driver's remaining delivery jobs on the phone dispatch view (#2677). */
export function phoneDriverPath(workerId: string): string {
  return `/dispatch?${PHONE_VIEW_PARAM}=${PHONE_VIEW.driver}&${PHONE_DRIVER_PARAM}=${encodeURIComponent(workerId)}`;
}

/**
 * ── The two namespaces (#2005) ─────────────────────────────────────────────
 *
 * Every audit tab lives under `/audit` and every webhook tab under `/webhooks`,
 * and each namespace is one entry of the 管理 sidebar group with its own strip
 * (`components/layout/nav-groups.ts`). Before #2005, the three webhook tabs sat
 * at `/webhooks`, `/admin/webhook-failures` and `/admin/webhook-health` — three
 * addresses that were not siblings of each other, presented as one surface.
 *
 * PATH SEGMENTS and not `?tab=`, under one rule: a path segment says "a
 * different thing", a query param says "the same thing, seen differently".
 * These tabs are different tables with different columns and their own detail
 * pages. The operations inbox goes the other way for the same reason — its two
 * tabs are one table plus a filter, so they ride `_tab=` (#2004).
 *
 * The bare namespace is a redirect to its strip's first tab (`/audit` →
 * operations, `/webhooks` → health), not another page: it is
 * the sidebar's entrance and the natural thing to type, and it must not become
 * a second address for the same view.
 */
export const ROUTES = {
  dispatch: {
    root: '/dispatch',
    jobs: '/dispatch/jobs',
    phoneInbox: `/dispatch?${PHONE_VIEW_PARAM}=${PHONE_VIEW.inbox}`,
    phoneSearch: `/dispatch?${PHONE_VIEW_PARAM}=${PHONE_VIEW.search}`,
  },
  audit: {
    root: '/audit',
    operations: '/audit/operations',
    changes: '/audit/changes',
  },
  webhooks: {
    root: '/webhooks',
    events: '/webhooks/events',
    failures: '/webhooks/failures',
    health: '/webhooks/health',
  },
  tenantSettings: {
    root: '/settings/tenant',
  },
} as const;

/**
 * Detail page for one `webhook_events` row.
 *
 * `eventId` is the internal `webhook_events` primary key (the ingest path
 * records the inserted row's `id`, never the provider's `provider_event_id`),
 * so no shape check and no lookup are needed to build the URL.
 */
export function webhookEventPath(eventId: string): string {
  return `${ROUTES.webhooks.events}/${eventId}`;
}

/** Detail page for one `webhook_failures` row. */
export function webhookFailurePath(failureId: string): string {
  return `${ROUTES.webhooks.failures}/${failureId}`;
}
